import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../hooks/useLanguage';
import { useTripData, useMinimumPrice } from '../../data/tripData';

const OurTrips: React.FC = () => {
  const { t } = useLanguage();
  
  const tripIds = ['summer-trip', 'winter-trip'];
  
  const trips = tripIds.map(tripId => {
    const tripData = useTripData(tripId);
    const { price: minimumPrice, loading: priceLoading } = useMinimumPrice(tripId);
    if (!tripData) return null;
    
    return {
      id: tripId,
      title: tripData.title,
      description: tripData.description,
      image: tripData.image,
      minimumPrice,
      priceLoading,
      duration: `${tripData.durationDays} ${tripData.durationDays === 1 ? t('common.day') : t('common.days')} - ${tripData.durationNights} ${tripData.durationNights === 1 ? t('common.night') : t('common.nights')}`,
      link: `/trip/${tripId}`
    };
  }).filter((trip): trip is NonNullable<typeof trip> => trip !== null);

  return (
    <section id="trips" className="deals-section py-16 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <header className="section-header text-center mb-12">
          <h2 className="section-title text-4xl font-bold text-gray-900 mb-4">
            {t('landing.trips.title')}
          </h2>
          <div className="section-content">
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('landing.trips.subtitle')}
            </p>
          </div>
        </header>

        {/* Trips Grid */}
        <div className="space-y-8 max-w-4xl mx-auto">
          {trips.map((trip) => (
            <div key={trip.id} className="col">
              <div className="col-holder bg-white rounded-lg shadow-lg overflow-hidden flex flex-col md:flex-row hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 relative">
                {/* Image Holder - Full width on mobile, half width on desktop */}
                <div className="img-holder w-full md:w-1/2 relative">
                  <Link to={trip.link}>
                    <img 
                      src={trip.image} 
                      alt={trip.title}
                      className="w-full h-64 md:h-full object-cover"
                    />
                  </Link>
                </div>

                {/* Text Holder - Full width on mobile, half width on desktop */}
                <div className="text-holder w-full md:w-1/2 p-6 flex flex-col justify-center">
                  <div className="trip-info">
                    {/* Duration and Price Badges - Side by side above title */}
                    <div className="flex gap-3 mb-8">
                      {/* Duration Badge - Blue rounded box */}
                      <div className="bg-blue-500 text-white px-3 py-2 rounded-lg shadow-lg">
                        <span className="text-sm font-semibold">{trip.duration}</span>
                      </div>
                      {/* Price Badge - Green rounded box */}
                      {!trip.priceLoading && trip.minimumPrice && (
                        <div className="bg-green-500 text-white px-3 py-2 rounded-lg shadow-lg">
                          <span className="text-sm font-semibold">
                            {t('common.startingFrom')} {trip.minimumPrice}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <h2 className="title text-2xl font-bold mb-3">
                      <Link to={trip.link} className="text-gray-900 hover:text-blue-600 transition-colors duration-300">
                        {trip.title}
                      </Link>
                    </h2>
                    
                    <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                      {trip.description}
                    </p>
                  </div>

                  <div className="btn-holder">
                    <Link 
                      to={trip.link} 
                      className="primary-btn readmore-btn bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-bold py-3 px-6 rounded-full transition-all duration-300 transform hover:scale-105 inline-block"
                    >
                      {t('common.viewDetails')}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default OurTrips; 